import type { GitStackedAction, VcsStatusResult } from "@threadlines/contracts";
import { isGitRepositoryMetadataCorruptionErrorMessage } from "@threadlines/shared/git";

export type SourceControlPrimaryActionIcon = "sparkles" | "upload";

export interface CommitGraphErrorPresentation {
  readonly title: string;
  readonly description: string | null;
  readonly repairCommand: string | null;
}

export interface SourceControlPrimaryAction {
  readonly action: Extract<GitStackedAction, "commit_push" | "push">;
  readonly label: string;
  readonly disabledReason: string | null;
  readonly icon: SourceControlPrimaryActionIcon;
}

export interface SourceControlFileTreeFileInput {
  readonly path: string;
  readonly insertions: number;
  readonly deletions: number;
}

export interface SourceControlFileTreeDirectoryNode<TFile extends SourceControlFileTreeFileInput> {
  readonly kind: "directory";
  readonly name: string;
  readonly path: string;
  readonly insertions: number;
  readonly deletions: number;
  readonly fileCount: number;
  readonly children: Array<SourceControlFileTreeNode<TFile>>;
}

export interface SourceControlFileTreeFileNode<TFile extends SourceControlFileTreeFileInput> {
  readonly kind: "file";
  readonly name: string;
  readonly path: string;
  readonly insertions: number;
  readonly deletions: number;
  readonly file: TFile;
}

export type SourceControlFileTreeNode<TFile extends SourceControlFileTreeFileInput> =
  | SourceControlFileTreeDirectoryNode<TFile>
  | SourceControlFileTreeFileNode<TFile>;

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : typeof error === "string" ? error : "";
}

export function resolveCommitGraphErrorPresentation(error: unknown): CommitGraphErrorPresentation {
  const message = errorMessage(error);
  if (isGitRepositoryMetadataCorruptionErrorMessage(message)) {
    return {
      title: "Local Git metadata needs repair",
      description: "Git reported corrupt or missing objects. Repair the repository, then retry.",
      repairCommand: "git fetch --refetch --prune origin",
    };
  }

  return {
    title: "Graph failed to load",
    description: null,
    repairCommand: null,
  };
}

interface MutableSourceControlFileTreeDirectory<TFile extends SourceControlFileTreeFileInput> {
  name: string;
  path: string;
  insertions: number;
  deletions: number;
  fileCount: number;
  directories: Map<string, MutableSourceControlFileTreeDirectory<TFile>>;
  files: Array<SourceControlFileTreeFileNode<TFile>>;
}

const SORT_LOCALE_OPTIONS: Intl.CollatorOptions = { numeric: true, sensitivity: "base" };

function normalizePathSegments(pathValue: string): string[] {
  return pathValue
    .replaceAll("\\", "/")
    .split("/")
    .filter((segment) => segment.length > 0);
}

function compareSourceControlTreeEntries(
  a: { readonly name: string },
  b: { readonly name: string },
) {
  return a.name.localeCompare(b.name, undefined, SORT_LOCALE_OPTIONS);
}

function compactSourceControlDirectoryNode<TFile extends SourceControlFileTreeFileInput>(
  node: SourceControlFileTreeDirectoryNode<TFile>,
): SourceControlFileTreeDirectoryNode<TFile> {
  const compactedChildren = node.children.map((child) =>
    child.kind === "directory" ? compactSourceControlDirectoryNode(child) : child,
  );

  let compactedNode: SourceControlFileTreeDirectoryNode<TFile> = {
    kind: "directory",
    name: node.name,
    path: node.path,
    insertions: node.insertions,
    deletions: node.deletions,
    fileCount: node.fileCount,
    children: compactedChildren,
  };

  while (compactedNode.children.length === 1 && compactedNode.children[0]?.kind === "directory") {
    const onlyChild = compactedNode.children[0];
    compactedNode = {
      kind: "directory",
      name: `${compactedNode.name}/${onlyChild.name}`,
      path: onlyChild.path,
      insertions: onlyChild.insertions,
      deletions: onlyChild.deletions,
      fileCount: onlyChild.fileCount,
      children: onlyChild.children,
    };
  }

  return compactedNode;
}

function toSourceControlFileTreeNodes<TFile extends SourceControlFileTreeFileInput>(
  directory: MutableSourceControlFileTreeDirectory<TFile>,
): Array<SourceControlFileTreeNode<TFile>> {
  const subdirectories = Array.from(directory.directories.values())
    .toSorted(compareSourceControlTreeEntries)
    .map<SourceControlFileTreeDirectoryNode<TFile>>((subdirectory) => ({
      kind: "directory",
      name: subdirectory.name,
      path: subdirectory.path,
      insertions: subdirectory.insertions,
      deletions: subdirectory.deletions,
      fileCount: subdirectory.fileCount,
      children: toSourceControlFileTreeNodes(subdirectory),
    }))
    .map((subdirectory) => compactSourceControlDirectoryNode(subdirectory));

  return [...subdirectories, ...directory.files.toSorted(compareSourceControlTreeEntries)];
}

export function buildSourceControlFileTree<TFile extends SourceControlFileTreeFileInput>(
  files: readonly TFile[],
): Array<SourceControlFileTreeNode<TFile>> {
  const root: MutableSourceControlFileTreeDirectory<TFile> = {
    name: "",
    path: "",
    insertions: 0,
    deletions: 0,
    fileCount: 0,
    directories: new Map(),
    files: [],
  };

  for (const file of files) {
    const segments = normalizePathSegments(file.path);
    const fileName = segments.at(-1);
    if (!fileName) {
      continue;
    }

    let currentDirectory = root;
    const ancestors: Array<MutableSourceControlFileTreeDirectory<TFile>> = [root];

    for (const segment of segments.slice(0, -1)) {
      const nextPath = currentDirectory.path ? `${currentDirectory.path}/${segment}` : segment;
      const existingDirectory = currentDirectory.directories.get(segment);
      if (existingDirectory) {
        currentDirectory = existingDirectory;
      } else {
        const createdDirectory: MutableSourceControlFileTreeDirectory<TFile> = {
          name: segment,
          path: nextPath,
          insertions: 0,
          deletions: 0,
          fileCount: 0,
          directories: new Map(),
          files: [],
        };
        currentDirectory.directories.set(segment, createdDirectory);
        currentDirectory = createdDirectory;
      }
      ancestors.push(currentDirectory);
    }

    const normalizedPath = segments.join("/");
    currentDirectory.files.push({
      kind: "file",
      name: fileName,
      path: normalizedPath,
      insertions: file.insertions,
      deletions: file.deletions,
      file,
    });

    for (const ancestor of ancestors) {
      ancestor.insertions += file.insertions;
      ancestor.deletions += file.deletions;
      ancestor.fileCount += 1;
    }
  }

  return toSourceControlFileTreeNodes(root);
}

export function collectSourceControlFileTreeDirectoryPaths<
  TFile extends SourceControlFileTreeFileInput,
>(nodes: readonly SourceControlFileTreeNode<TFile>[]): string[] {
  const paths: string[] = [];
  for (const node of nodes) {
    if (node.kind !== "directory") {
      continue;
    }
    paths.push(node.path);
    for (const childPath of collectSourceControlFileTreeDirectoryPaths(node.children)) {
      paths.push(childPath);
    }
  }
  return paths;
}

export function formatCommitCount(count: number): string {
  return count === 1 ? "1 commit" : `${count} commits`;
}

export function resolveSourceControlPrimaryAction(input: {
  readonly status: VcsStatusResult | null;
  readonly hasCommitMessage: boolean;
  readonly commitAndPushDisabledReason: string | null;
  readonly pushDisabledReason: string | null;
}): SourceControlPrimaryAction {
  const status = input.status;
  if (status?.isRepo && !status.hasWorkingTreeChanges && input.pushDisabledReason === null) {
    const shouldPublishBranch =
      !status.hasUpstream && status.hasPrimaryRemote && !status.isDefaultRef;
    return {
      action: "push",
      label: shouldPublishBranch
        ? "Publish branch"
        : status.aheadCount > 0
          ? `Push ${formatCommitCount(status.aheadCount)}`
          : "Push",
      disabledReason: input.pushDisabledReason,
      icon: "upload",
    };
  }

  return {
    action: "commit_push",
    label: input.hasCommitMessage ? "Commit & push" : "Generate, commit & push",
    disabledReason: input.commitAndPushDisabledReason,
    icon: "sparkles",
  };
}

export function formatCommitGraphTimestamp(value: string, now = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const elapsedSeconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));
  if (elapsedSeconds < 60) {
    return "just now";
  }

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m ago`;
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `${elapsedHours}h ago`;
  }

  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) {
    return `${elapsedDays}d ago`;
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function formatCommitGraphDateTime(
  value: string,
  locale?: string,
  timeZone?: string,
): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    ...(timeZone ? { timeZone } : {}),
  });
}

export function formatCommitGraphParentSummary(parentCount: number): string {
  if (parentCount <= 0) {
    return "Root commit";
  }
  if (parentCount === 1) {
    return "1 parent";
  }
  return `${parentCount} parents - merge commit`;
}

export type CommitGraphRefKind = "branch" | "current" | "remote" | "tag";

export interface CommitGraphTopologyCommit {
  readonly sha: string;
  readonly parents: readonly string[];
  readonly refs?: readonly string[];
}

export interface CommitGraphLanePath {
  readonly fromLane: number;
  readonly toLane: number;
}

export interface CommitGraphLaneLayout {
  readonly lane: number;
  readonly topLanes: readonly number[];
  readonly bottomLanes: readonly number[];
  readonly parentPaths: readonly CommitGraphLanePath[];
  readonly laneCount: number;
  readonly isNewTip: boolean;
}

export interface CommitGraphDisplayRow<TCommit extends CommitGraphTopologyCommit> {
  readonly commit: TCommit;
  readonly layout: CommitGraphLaneLayout;
  readonly visibleRefs: readonly string[];
}

export function normalizeCommitGraphRefName(refName: string): string {
  return refName
    .trim()
    .replace(/^refs\/heads\//, "")
    .replace(/^refs\/remotes\//, "")
    .replace(/^refs\/tags\//, "")
    .replace(/^tags\//, "");
}

export function isVisibleCommitGraphRef(refName: string): boolean {
  const trimmed = refName.trim();
  if (trimmed.length === 0 || trimmed === "HEAD" || trimmed.includes(" -> ")) {
    return false;
  }

  const normalized = normalizeCommitGraphRefName(trimmed);
  return !/^[^/]+\/HEAD$/i.test(normalized);
}

export function getVisibleCommitGraphRefs(refs: readonly string[]): string[] {
  const seen = new Set<string>();
  return refs.filter((ref) => {
    if (!isVisibleCommitGraphRef(ref)) {
      return false;
    }
    const normalized = normalizeCommitGraphRefName(ref);
    if (seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
}

function isTagCommitGraphRef(refName: string): boolean {
  const normalized = normalizeCommitGraphRefName(refName);
  return (
    refName.startsWith("refs/tags/") ||
    refName.startsWith("tags/") ||
    normalized.startsWith("tag/") ||
    /^v\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/.test(normalized)
  );
}

function isRemoteCommitGraphRef(refName: string): boolean {
  const normalized = normalizeCommitGraphRefName(refName);
  return (
    refName.startsWith("refs/remotes/") ||
    normalized.startsWith("origin/") ||
    normalized.startsWith("upstream/") ||
    normalized.startsWith("fork/")
  );
}

function isLocalBranchCommitGraphRef(refName: string): boolean {
  return !isTagCommitGraphRef(refName) && !isRemoteCommitGraphRef(refName);
}

export function getCommitGraphRefKind(
  refName: string,
  currentBranch: string | null | undefined,
): CommitGraphRefKind {
  const normalized = normalizeCommitGraphRefName(refName);
  if (currentBranch && (normalized === currentBranch || normalized === `origin/${currentBranch}`)) {
    return "current";
  }
  if (isTagCommitGraphRef(refName)) {
    return "tag";
  }
  if (isRemoteCommitGraphRef(refName)) {
    return "remote";
  }
  return "branch";
}

// "synced": local branch whose same-named remote points at the same commit
// (trailing cloud segment). "remote": remote-only ref (leading cloud).
export type CommitGraphRefCloudBadge = "none" | "synced" | "remote";

export interface CommitGraphDisplayRef {
  readonly refName: string;
  readonly label: string;
  readonly kind: CommitGraphRefKind;
  readonly cloudBadge: CommitGraphRefCloudBadge;
}

// Lower value renders first; the leading chip is the one that survives
// truncation in narrow panels, so order by how actionable the ref is.
const COMMIT_GRAPH_REF_KIND_PRIORITY: Record<CommitGraphRefKind, number> = {
  current: 0,
  branch: 1,
  tag: 2,
  remote: 3,
};

function sortCommitGraphDisplayRefs(displayRefs: CommitGraphDisplayRef[]): CommitGraphDisplayRef[] {
  return displayRefs.toSorted(
    (a, b) => COMMIT_GRAPH_REF_KIND_PRIORITY[a.kind] - COMMIT_GRAPH_REF_KIND_PRIORITY[b.kind],
  );
}

// The leading cloud already marks these chips as remote, so the primary
// remote's prefix is redundant text; other remotes (upstream, fork) are rare
// enough that naming them is worth the width.
const PRIMARY_REMOTE_REF_PREFIX = "origin/";

// Chip models for a commit row: a remote branch pointing at the same commit as
// its same-named local branch collapses into the local chip (cloudBadge
// "synced") instead of costing a second chip, remaining remote-only refs drop
// the origin/ prefix in favor of a leading cloud (cloudBadge "remote"), and
// the result is sorted so the most relevant ref survives truncation.
export function buildCommitGraphDisplayRefs(
  refs: readonly string[],
  currentBranch: string | null | undefined,
): CommitGraphDisplayRef[] {
  const visibleRefs = getVisibleCommitGraphRefs(refs);
  const localBranchLabels = new Set(
    visibleRefs.filter(isLocalBranchCommitGraphRef).map(normalizeCommitGraphRefName),
  );

  const syncedLocalBranchLabels = new Set<string>();
  const unmergedRefs: Array<{ readonly refName: string; readonly label: string }> = [];
  for (const refName of visibleRefs) {
    const label = normalizeCommitGraphRefName(refName);
    if (isRemoteCommitGraphRef(refName)) {
      const localLabel = label.slice(label.indexOf("/") + 1);
      if (localBranchLabels.has(localLabel)) {
        syncedLocalBranchLabels.add(localLabel);
        continue;
      }
    }
    unmergedRefs.push({ refName, label });
  }

  return sortCommitGraphDisplayRefs(
    unmergedRefs.map(({ refName, label }) => {
      const isRemote = isRemoteCommitGraphRef(refName);
      return {
        refName,
        label:
          isRemote && label.startsWith(PRIMARY_REMOTE_REF_PREFIX)
            ? label.slice(PRIMARY_REMOTE_REF_PREFIX.length)
            : label,
        kind: getCommitGraphRefKind(refName, currentBranch),
        cloudBadge: isRemote
          ? ("remote" as const)
          : isLocalBranchCommitGraphRef(refName) && syncedLocalBranchLabels.has(label)
            ? ("synced" as const)
            : ("none" as const),
      };
    }),
  );
}

// Chip models for the commit details card: same classification and ordering as
// the row chips, but every ref stays its own chip with its full label, so the
// card spells out exactly which refs point at the commit. Remote refs keep the
// leading cloud next to their unstripped name — the deliberate redundancy is
// what teaches that the rows' cloud stands for the origin/ prefix.
export function buildCommitGraphDetailRefs(
  refs: readonly string[],
  currentBranch: string | null | undefined,
): CommitGraphDisplayRef[] {
  return sortCommitGraphDisplayRefs(
    getVisibleCommitGraphRefs(refs).map((refName) => ({
      refName,
      label: normalizeCommitGraphRefName(refName),
      kind: getCommitGraphRefKind(refName, currentBranch),
      cloudBadge: isRemoteCommitGraphRef(refName) ? ("remote" as const) : ("none" as const),
    })),
  );
}

// Two chips side by side are only worth their space when both labels stay
// legible; past this combined length the second chip would truncate into an
// unreadable sliver, so it folds into the +N badge instead.
const COMMIT_GRAPH_ROW_REF_CHAR_BUDGET = 20;

export interface CommitGraphRowRefs {
  readonly rendered: readonly CommitGraphDisplayRef[];
  readonly hiddenCount: number;
}

export function takeCommitGraphRowRefs(
  displayRefs: readonly CommitGraphDisplayRef[],
): CommitGraphRowRefs {
  const [first, second] = displayRefs;
  if (!first) {
    return { rendered: [], hiddenCount: 0 };
  }
  const rendered =
    second && first.label.length + second.label.length <= COMMIT_GRAPH_ROW_REF_CHAR_BUDGET
      ? [first, second]
      : [first];
  return { rendered, hiddenCount: displayRefs.length - rendered.length };
}

function occupiedLaneIndexes(lanes: ReadonlyArray<string | null>): number[] {
  const indexes: number[] = [];
  for (let index = 0; index < lanes.length; index += 1) {
    if (lanes[index] !== null) {
      indexes.push(index);
    }
  }
  return indexes;
}

export function buildCommitGraphRows<TCommit extends CommitGraphTopologyCommit>(
  commits: readonly TCommit[],
): Array<CommitGraphDisplayRow<TCommit>> {
  // Each lane holds the sha it is waiting for. A closed lane leaves a null
  // hole rather than compacting: surviving lanes must keep their index so the
  // lines already drawn in the rows above stay at the same x-position (and
  // color). Compacting shifted every lane right of a merge, detaching those
  // lines mid-graph.
  let activeLanes: Array<string | null> = [];
  const rows: Array<CommitGraphDisplayRow<TCommit>> = [];

  for (const commit of commits) {
    let lane = activeLanes.indexOf(commit.sha);
    const isNewTip = lane < 0;
    if (isNewTip) {
      const freeLane = activeLanes.indexOf(null);
      lane = freeLane >= 0 ? freeLane : activeLanes.length;
    }

    const topLanes = occupiedLaneIndexes(activeLanes);
    const nextLanes: Array<string | null> = [...activeLanes];
    nextLanes[lane] = commit.sha;
    const parentPaths: CommitGraphLanePath[] = [];

    if (commit.parents.length === 0) {
      nextLanes[lane] = null;
    } else {
      const [firstParent, ...additionalParents] = commit.parents;
      if (firstParent) {
        const existingParentLane = nextLanes.findIndex(
          (sha, index) => index !== lane && sha === firstParent,
        );
        if (existingParentLane >= 0) {
          // The parent keeps the leftmost of the two lanes so the longer-lived
          // line continues straight while the other lane curves into it.
          const toLane = Math.min(lane, existingParentLane);
          nextLanes[toLane] = firstParent;
          nextLanes[Math.max(lane, existingParentLane)] = null;
          if (lane < existingParentLane) {
            parentPaths.push({ fromLane: lane, toLane });
            parentPaths.push({ fromLane: existingParentLane, toLane });
          } else {
            parentPaths.push({ fromLane: lane, toLane });
          }
        } else {
          nextLanes[lane] = firstParent;
          parentPaths.push({ fromLane: lane, toLane: lane });
        }
      }

      for (const parent of additionalParents) {
        const existingParentLane = nextLanes.indexOf(parent);
        if (existingParentLane >= 0) {
          parentPaths.push({ fromLane: lane, toLane: existingParentLane });
          continue;
        }

        // Reuse a hole only if it was already free above this row; a lane
        // freed by this very commit still has its old line in the top half,
        // which a curve opening into it would appear to continue.
        let parentLane = nextLanes.length;
        for (let index = 0; index < nextLanes.length; index += 1) {
          if (nextLanes[index] === null && activeLanes[index] === null) {
            parentLane = index;
            break;
          }
        }
        nextLanes[parentLane] = parent;
        parentPaths.push({ fromLane: lane, toLane: parentLane });
      }
    }

    while (nextLanes.length > 0 && nextLanes[nextLanes.length - 1] === null) {
      nextLanes.pop();
    }

    const bottomLanes = occupiedLaneIndexes(nextLanes);
    const rowLaneCount = Math.max(
      1,
      lane + 1,
      topLanes.length === 0 ? 0 : Math.max(...topLanes) + 1,
      bottomLanes.length === 0 ? 0 : Math.max(...bottomLanes) + 1,
      parentPaths.length === 0
        ? 0
        : Math.max(...parentPaths.flatMap((path) => [path.fromLane, path.toLane])) + 1,
    );

    rows.push({
      commit,
      layout: {
        lane,
        topLanes,
        bottomLanes,
        parentPaths,
        // Per-row on purpose: the text column starts right after this row's
        // rightmost active lane, so quiet stretches of history don't inherit
        // the indent of the busiest merge region elsewhere in the graph.
        laneCount: rowLaneCount,
        isNewTip,
      },
      visibleRefs: getVisibleCommitGraphRefs(commit.refs ?? []),
    });
    activeLanes = nextLanes;
  }

  return rows;
}
